import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'

import { StickyBox } from 'components/Box'
import { CommonFormContextProps, SaveButton, useCommonFormContextProps } from 'components/Form'
import { PageHeader } from 'components/Header'
import Tabs, { getPage, Tab } from 'components/Tabs'
import {
  DefaultsPageContext,
  DefaultsPageContextType,
} from 'features/globalSetup/contexts/DefaultsPageContext'
import { WorkspacePrebidConfiguration } from 'features/globalSetup/types/workspacePrebidConfiguration'
import { useAbility } from 'providers/casl'
import { ROUTES } from 'routes'
import { DefaultsPageTab, DefaultsPageUrlParams } from './types'
import { useDefaultsTabs } from './useTabs'

export const DefaultsPage = (): JSX.Element => {
  const { t } = useTranslation(['features/globalSetup', 'common'])
  const ability = useAbility()
  const tabs: Tab<DefaultsPageTab>[] = useDefaultsTabs()
  const { tab: currentTab, workspaceId } = useParams<DefaultsPageUrlParams>()
  const [workspaceConfig, setWorkspaceConfig] = useState<WorkspacePrebidConfiguration>()

  const commonContextProps: CommonFormContextProps = useCommonFormContextProps({
    formId: 'global-setup-defaults-form',
  })
  const contextProps: DefaultsPageContextType = {
    ...commonContextProps,
    prebid: {
      setWorkspaceConfig,
      workspaceConfig,
    },
  }

  if (!workspaceId || !currentTab) {
    return <Navigate to={ROUTES.BASE.NOT_FOUND} />
  }

  return (
    <DefaultsPageContext.Provider value={contextProps}>
      <StickyBox>
        <DefaultsPageContext.Consumer>
          {(ctxProps: DefaultsPageContextType): JSX.Element => (
            <PageHeader
              actions={
                <SaveButton
                  {...ctxProps}
                  can={ability.can('update', 'GlobalSetupFeature-defaults-prebid')}
                />
              }
              title={t('defaults.header')}
            />
          )}
        </DefaultsPageContext.Consumer>

        <Tabs
          path={ROUTES.GLOBAL_SETUP.DEFAULTS.ROOT}
          tabs={tabs}
        />
      </StickyBox>

      {getPage<DefaultsPageTab>(tabs, currentTab, 'prebid-stack')}
    </DefaultsPageContext.Provider>
  )
}
